import { useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router";
import {
  useLibraryNode,
  useEntityType,
  useEntityTypes,
  useEntities,
  useCreateEntity,
  useUpdateEntity,
  useUpdateEntityType,
  useArchiveNode,
} from "../../lib/hooks/use-kuvio";
import { Card, EmptyState, ConfirmModal, showToast } from "../components/primitives";
import { TableHeader } from "../components/table/TableHeader";
import { TableSearch } from "../components/table/TableSearch";
import { TableGrid } from "../components/table/TableGrid";
import { TableFooter } from "../components/table/TableFooter";
import { AddColumnModal } from "../components/table/AddColumnModal";
import { RenameTableModal } from "../components/table/RenameTableModal";
import { ConfirmDeleteTableModal, type BackRefType } from "../components/table/ConfirmDeleteTableModal";
import { WorkflowEditorModal } from "../components/table/WorkflowEditorModal";
import { FilterChipRail, type TableFilter } from "../components/table/FilterChipRail";
import { ActionMenu } from "../components/shell/ActionMenu";
import { ManageAccessModal } from "../components/scope/ManageAccessModal";
import { ChangeOwnerModal } from "../components/table/ChangeOwnerModal";
import { useAuth } from "../../lib/hooks/use-auth";
import { useRightRail } from "../components/shell/RightRailContext";
import { useChatScope } from "../hooks/use-chat-scope";
import { useEntityNameResolver } from "../hooks/use-entity-names";
import { getColumnsFor, buildVirtualColumns } from "../lib/v3-columns";
import type { EntityRecord, LibraryNodeDetail } from "../../lib/types";

function resolveFieldValue(row: EntityRecord, field: string): unknown {
  if (field.includes(".")) {
    const parts = field.split(".");
    let current: unknown = row;
    for (const part of parts) {
      if (current == null || typeof current !== "object") return undefined;
      current = (current as Record<string, unknown>)[part];
    }
    return current;
  }
  return row[field];
}

export function TablePage() {
  const { nodeId, tableId } = useParams<{ nodeId: string; tableId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState<{ column: string; dir: "asc" | "desc" } | null>(null);
  const [mutatingRows, setMutatingRows] = useState<Set<string>>(new Set());
  const [newRowId, setNewRowId] = useState<string | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [addColumnOpen, setAddColumnOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteTableOpen, setDeleteTableOpen] = useState(false);
  const [workflowOpen, setWorkflowOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [changeOwnerOpen, setChangeOwnerOpen] = useState(false);
  const { user, currentWorkspaceId } = useAuth();
  const isOwner = user?.workspaces.find((w) => w.id === currentWorkspaceId)?.role === "owner";
  const archiveNode = useArchiveNode();

  // Sprint 9a — tables need every pixel: collapse the right rail so the grid
  // gets the full available width.
  useRightRail(null);

  const searchQuery = searchParams.get("q") ?? "";
  function setSearchQuery(q: string) {
    const next = new URLSearchParams(searchParams);
    if (q) {
      next.set("q", q);
    } else {
      next.delete("q");
    }
    setSearchParams(next);
  }

  const tableNode = useLibraryNode(tableId);
  const table = tableNode.data as LibraryNodeDetail | undefined;
  const entityTypeSlug = table?.entity_type_slug;
  const entityType = useEntityType(entityTypeSlug ?? undefined);
  const allTypes = useEntityTypes();

  // Sprint 22 — scope the cockpit chat to this entity type. The chat node is the
  // table node (scope.id = tableId), so the worker builds the table assistant
  // prompt; "Show me the top 5 by revenue" lands on the right list_* tool.
  useChatScope(
    tableId
      ? { kind: "table", id: tableId, type_slug: entityTypeSlug ?? undefined, label: table?.name ?? "this table" }
      : { kind: "workspace", label: "This workspace" },
    null,
    [tableId, table?.name, entityTypeSlug],
  );

  // Sprint 14a — other tables whose reference fields point at this one. Surfaced
  // in the delete-table confirmation and cleared (via unlink) before deletion.
  const backRefTypes: BackRefType[] = useMemo(() => {
    const slug = entityType.data?.slug;
    if (!slug || !allTypes.data) return [];
    return allTypes.data
      .filter((t) => t.slug !== slug)
      .map((t) => ({
        name: t.name,
        slug: t.slug,
        fields: t.fields
          .filter((f) => (f.type === "reference" || f.type === "references") && f.target === slug)
          .map((f) => f.name),
      }))
      .filter((t) => t.fields.length > 0);
  }, [allTypes.data, entityType.data?.slug]);

  const parentNode = useLibraryNode(nodeId);
  const parentAgent = parentNode.data
    ? { id: parentNode.data.id, name: parentNode.data.name, persona: "teal" }
    : null;

  const filters = parseFiltersFromUrl(searchParams);
  const filterRecord = filters.reduce(
    (acc, f) => ({ ...acc, [f.column]: f.value }),
    {} as Record<string, string>,
  );

  const entitiesQuery = useEntities(entityType.data, filterRecord);
  const rows = (entitiesQuery.data as unknown as { entities: EntityRecord[] })?.entities ?? (entitiesQuery.data as unknown as EntityRecord[]) ?? [];

  const createEntity = useCreateEntity(entityType.data);
  const updateEntity = useUpdateEntity(entityType.data);
  const updateEntityType = useUpdateEntityType();

  const schemaColumns = entityType.data ? getColumnsFor(entityType.data) : [];
  const virtual = buildVirtualColumns();
  const columns = [...virtual.before, ...schemaColumns, ...virtual.after];

  // Sprint 12 — resolve FK ids to display values so reference cells render the
  // target's name (e.g. "Sarah Chen") rather than its id.
  const refTargetSlugs = schemaColumns
    .filter((c) => (c.type === "reference" || c.type === "references") && c.target)
    .map((c) => c.target as string);
  const resolveRef = useEntityNameResolver(refTargetSlugs);

  const handleCellSave = useCallback(
    (rowId: string, field: string, value: unknown) => {
      setMutatingRows((prev) => new Set(prev).add(rowId));
      updateEntity.mutate(
        { id: rowId, fields: { [field]: value } },
        {
          onSettled: () => {
            setTimeout(() => {
              setMutatingRows((prev) => {
                const next = new Set(prev);
                next.delete(rowId);
                return next;
              });
            }, 200);
          },
        },
      );
    },
    [updateEntity],
  );

  const handleAddRow = useCallback(() => {
    const defaults: Record<string, unknown> = {};
    if (entityType.data) {
      for (const f of entityType.data.fields) {
        if (f.required) {
          defaults[f.name] = f.type === "number" || f.type === "currency" ? 0 : "Untitled";
        }
      }
    }
    createEntity.mutate(defaults, {
      onSuccess: (entity: EntityRecord) => {
        setNewRowId(entity.id);
        setTimeout(() => setNewRowId(null), 2000);
      },
    });
  }, [createEntity, entityType.data]);

  function handleFiltersChange(next: TableFilter[]) {
    const params: Record<string, string> = {};
    next.forEach((f) => {
      const key = `filter`;
      const existing = params[key];
      params[key] = existing
        ? `${existing}&filter=${f.column}:${f.value}`
        : `${f.column}:${f.value}`;
    });
    if (next.length === 0) {
      setSearchParams({});
    } else {
      const sp = new URLSearchParams();
      next.forEach((f) => sp.append("filter", `${f.column}:${f.value}`));
      setSearchParams(sp);
    }
  }

  function handleFilterColumn(column: string) {
    const col = columns.find((c) => c.field === column);
    if (col?.choices && col.choices.length > 0) {
      handleFiltersChange([...filters, { column, value: col.choices[0] ?? "" }]);
    }
  }

  const searchableColumnFields = useMemo(() => {
    const textTypes = new Set(["text", "email", "phone", "reference", "string"]);
    return columns
      .filter((c) => c.isTitle || textTypes.has(c.type))
      .map((c) => c.field);
  }, [columns]);

  const filteredRows = useMemo(() => {
    if (!searchQuery) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter((row) => {
      const parts: string[] = [row.id];
      for (const f of searchableColumnFields) {
        const val = resolveFieldValue(row, f);
        if (val != null) parts.push(String(val));
      }
      return parts.join(" ").toLowerCase().includes(q);
    });
  }, [rows, searchQuery, searchableColumnFields]);

  // Loading skeleton
  if (tableNode.isLoading || entityType.isLoading) {
    return (
      <div style={{ fontFamily: "var(--font-sans)" }}>
        <div style={{
          background: "var(--color-background-secondary)",
          borderRadius: "var(--border-radius-lg)",
          height: 60,
          marginBottom: 12,
        }} />
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            style={{
              background: "var(--color-background-secondary)",
              borderRadius: "var(--border-radius-sm)",
              height: 40,
              marginBottom: 2,
            }}
          />
        ))}
      </div>
    );
  }

  // Error state
  if (tableNode.error || !table || entityType.error || !entityType.data) {
    return (
      <Card>
        <EmptyState>
          {tableNode.error?.message ?? entityType.error?.message ?? "Table not found"}
        </EmptyState>
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <button
            onClick={() => {
              tableNode.refetch();
              entityType.refetch();
            }}
            style={{
              fontSize: 13,
              color: "var(--color-text-info)",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontFamily: "var(--font-sans)",
            }}
          >
            Retry
          </button>
        </div>
      </Card>
    );
  }

  const plural = entityType.data.plural ?? entityType.data.slug + "s";
  const singular = entityType.data.name ?? entityType.data.slug;

  const ownerAgentId = nodeId ?? table?.parent_id;

  // Sprint 14 — interaction-history capability. Default on; toggling is
  // non-destructive (existing Activity rows survive, just hidden from this
  // type's row detail while off).
  const tracksActivity = entityType.data.activity_tracking !== false;
  function toggleActivityTracking() {
    updateEntityType.mutate(
      {
        slug: entityType.data!.slug,
        patch: { activity_tracking: !tracksActivity },
        expectedVersion: entityType.data!._version,
      },
      {
        onSuccess: () => showToast(tracksActivity ? "Interaction tracking turned off" : "Interaction tracking turned on"),
        onError: (e) => showToast(`Couldn't update: ${e instanceof Error ? e.message : "failed"}`, "error", true),
      },
    );
  }

  const isScope = !!entityType.data.is_scope;
  function toggleIsScope() {
    updateEntityType.mutate(
      {
        slug: entityType.data!.slug,
        patch: { is_scope: !isScope },
        expectedVersion: entityType.data!._version,
      },
      {
        onSuccess: () => showToast(isScope ? "Scope dimension removed" : "Now usable as a scope dimension"),
        onError: (e) => showToast(`Couldn't update: ${e instanceof Error ? e.message : "failed"}`, "error", true),
      },
    );
  }

  const tableActionMenu = (
    <ActionMenu
      items={[
        {
          label: "Rename table",
          icon: "pencil",
          onClick: () => setRenameOpen(true),
        },
        {
          label: entityType.data.workflow ? "Edit workflow…" : "Add workflow…",
          icon: "route",
          onClick: () => setWorkflowOpen(true),
        },
        {
          label: tracksActivity ? "Stop tracking interactions" : "Track interactions on this type",
          icon: tracksActivity ? "history-toggle" : "history",
          onClick: toggleActivityTracking,
        },
        {
          label: isScope ? "Remove as scope dimension" : "Use as scope dimension",
          icon: isScope ? "focus-2" : "focus",
          onClick: toggleIsScope,
        },
        // Sprint 14c — owner-only table access management.
        ...(isOwner
          ? [
              { label: "Manage access", icon: "lock", onClick: () => setAccessOpen(true) },
              { label: "Change owner…", icon: "user-edit", onClick: () => setChangeOwnerOpen(true) },
            ]
          : []),
        {
          label: "Archive table",
          icon: "archive",
          variant: "danger",
          onClick: () => setArchiveOpen(true),
        },
        {
          label: "Delete table",
          icon: "trash",
          variant: "danger",
          onClick: () => setDeleteTableOpen(true),
        },
      ]}
    />
  );

  const archiveModal = (
    <ConfirmModal
      open={archiveOpen}
      onOpenChange={setArchiveOpen}
      title={`Archive ${table.name}?`}
      message={`Archived tables disappear from the Owned Tables grid. The ${filteredRows.length} row${filteredRows.length === 1 ? "" : "s"} in this table ${filteredRows.length === 1 ? "is" : "are"} archived alongside it. Archived items are recoverable via MCP until purged.`}
      confirmLabel="Archive table"
      variant="danger"
      onConfirm={async () => {
        await archiveNode.mutateAsync(tableId!);
        navigate(ownerAgentId ? `/agents/${ownerAgentId}` : "/");
      }}
    />
  );

  // Sprint 14a — table-level admin modals. Bundled with the archive modal so every
  // render branch (main, search-empty, filter-empty) mounts the full set, since the
  // action menu that triggers them is present in all three.
  const tableModals = (
    <>
      {archiveModal}
      <ManageAccessModal
        open={accessOpen}
        onClose={() => setAccessOpen(false)}
        name={table.name}
        entityTypeSlug={entityType.data.slug}
        currentAllowedUsers={table.access_scope?.allowed_users ?? null}
      />
      <ChangeOwnerModal
        open={changeOwnerOpen}
        onClose={() => setChangeOwnerOpen(false)}
        entityTypeSlug={entityType.data.slug}
        entityTypeName={entityType.data.name}
        currentOwnerAgentId={entityType.data.owner_agent_id ?? undefined}
      />
      <RenameTableModal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        entityType={entityType.data}
        tableNodeId={tableId!}
        onRenamed={() => tableNode.refetch()}
      />
      <ConfirmDeleteTableModal
        open={deleteTableOpen}
        onClose={() => setDeleteTableOpen(false)}
        entityType={entityType.data}
        recordCount={entityType.data._record_count ?? filteredRows.length}
        backRefTypes={backRefTypes}
        onDeleted={() => {
          setDeleteTableOpen(false);
          navigate(ownerAgentId ? `/agents/${ownerAgentId}` : "/");
        }}
      />
      <WorkflowEditorModal
        open={workflowOpen}
        onClose={() => setWorkflowOpen(false)}
        entityType={entityType.data}
        onSaved={() => entityType.refetch()}
      />
    </>
  );

  // Empty after search
  if (searchQuery && filteredRows.length === 0) {
    return (
      <div style={{ fontFamily: "var(--font-sans)" }}>
        <TableHeader table={table} entityType={entityType.data} rowCount={0} parentAgent={parentAgent} actionMenu={tableActionMenu} />
        <TableSearch query={searchQuery} onChange={setSearchQuery} plural={plural} />
        <FilterChipRail filters={filters} columns={columns} onChange={handleFiltersChange} />
        <Card>
          <EmptyState>
            No {plural} match "{searchQuery}".
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => setSearchQuery("")}
                style={{ fontSize: 12, color: "var(--color-text-info)", background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font-sans)" }}
              >
                Clear search
              </button>
            </div>
          </EmptyState>
        </Card>
        {tableModals}
      </div>
    );
  }

  // Empty after filter
  if (filters.length > 0 && filteredRows.length === 0) {
    return (
      <div style={{ fontFamily: "var(--font-sans)" }}>
        <TableHeader table={table} entityType={entityType.data} rowCount={0} parentAgent={parentAgent} actionMenu={tableActionMenu} />
        <TableSearch query={searchQuery} onChange={setSearchQuery} plural={plural} />
        <FilterChipRail filters={filters} columns={columns} onChange={handleFiltersChange} />
        <Card>
          <EmptyState>
            No {plural} match these filters.
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => handleFiltersChange([])}
                style={{ fontSize: 12, color: "var(--color-text-info)", background: "none", border: "none", cursor: "pointer", fontFamily: "var(--font-sans)" }}
              >
                Clear filters
              </button>
            </div>
          </EmptyState>
        </Card>
        {tableModals}
      </div>
    );
  }

  const hasUserFields = schemaColumns.length > 0;
  const hasEditableFields = schemaColumns.some((c) => c.editable);

  const emptyTableState = filteredRows.length === 0 ? (
    <div style={{ fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.6 }}>
      {!hasUserFields ? (
        <>
          <div>Define your first column to start filling this table.</div>
          <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "center" }}>
            <button
              data-variant="primary"
              onClick={() => setAddColumnOpen(true)}
              style={{ fontSize: 12, padding: "6px 12px", display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <i className="ti ti-plus" style={{ fontSize: 12 }} aria-hidden="true" />
              Add column
            </button>
          </div>
        </>
      ) : (
        <>
          <div>No {plural} yet. Add a row to start, or define more columns first.</div>
          <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "center" }}>
            {hasEditableFields && (
              <button
                data-variant="primary"
                onClick={handleAddRow}
                disabled={createEntity.isPending}
                style={{ fontSize: 12, padding: "6px 12px", display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                <i className="ti ti-plus" style={{ fontSize: 12 }} aria-hidden="true" />
                {createEntity.isPending ? "Adding..." : `Add ${singular.toLowerCase()}`}
              </button>
            )}
            <button
              onClick={() => setAddColumnOpen(true)}
              style={{
                fontSize: 12,
                padding: "6px 12px",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "transparent",
                border: "0.5px solid var(--color-border-secondary)",
                borderRadius: "var(--border-radius-md)",
                color: "var(--color-text-secondary)",
                cursor: "pointer",
                fontFamily: "var(--font-sans)",
              }}
            >
              <i className="ti ti-plus" style={{ fontSize: 12 }} aria-hidden="true" />
              Add column
            </button>
          </div>
        </>
      )}
    </div>
  ) : undefined;

  return (
    <div style={{ fontFamily: "var(--font-sans)" }}>
      <TableHeader table={table} entityType={entityType.data} rowCount={filteredRows.length} parentAgent={parentAgent} actionMenu={tableActionMenu} />
      {filteredRows.length > 0 && (
        <TableSearch query={searchQuery} onChange={setSearchQuery} plural={plural} />
      )}
      {filteredRows.length > 0 && (
        <FilterChipRail filters={filters} columns={columns} onChange={handleFiltersChange} />
      )}
      <TableGrid
        columns={columns}
        rows={filteredRows}
        entityType={entityType.data}
        sort={sort}
        onSortChange={setSort}
        onCellSave={handleCellSave}
        mutatingRows={mutatingRows}
        onFilterColumn={handleFilterColumn}
        newRowId={newRowId}
        onColumnAdded={() => entityType.refetch()}
        emptyState={emptyTableState}
        resolveRef={resolveRef}
      />
      {filteredRows.length > 0 && (
        <TableFooter
          entityType={entityType.data}
          columns={columns}
          rows={filteredRows as unknown as Record<string, unknown>[]}
          onAddRow={handleAddRow}
        />
      )}

      {filteredRows.length > 0 && (
        <div style={{
          marginTop: 14,
          padding: "10px 12px",
          background: "var(--color-background-secondary)",
          borderRadius: "var(--border-radius-md)",
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
        }}>
          <i
            className="ti ti-bulb"
            style={{ fontSize: 14, color: "var(--color-text-tertiary)", marginTop: 2 }}
            aria-hidden="true"
          />
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
            Click any cell to edit. Click a column header to sort.{" "}
            <span style={{ color: "var(--color-text-primary)", fontWeight: 500 }}>
              No view designer.
            </span>{" "}
            If you want a different layout, ask Claude to render one.
          </div>
        </div>
      )}

      <AddColumnModal
        open={addColumnOpen}
        onOpenChange={setAddColumnOpen}
        entityType={entityType.data}
        onAdded={() => entityType.refetch()}
      />
      {tableModals}
    </div>
  );
}

function parseFiltersFromUrl(params: URLSearchParams): TableFilter[] {
  const all = params.getAll("filter");
  return all
    .map((raw) => {
      const idx = raw.indexOf(":");
      if (idx === -1) return null;
      return { column: raw.slice(0, idx), value: raw.slice(idx + 1) };
    })
    .filter(Boolean) as TableFilter[];
}
